import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/en/services/tracking-data-foundation")({
  head: () => {
    const h = pageHead("trackingDataFoundation", "en");
    return { meta: h.meta, links: h.links };
  },
  component: TrackingPage,
});

const steps = [
  { num: "01", title: "Analysis of existing tracking and data landscape", body: "We analyze how data is currently collected, processed, and interpreted. GA4 setup, captured events, differences between platform data and web analytics, consent mechanics, plausibility of numbers. Goal: transparency about which numbers can be trusted." },
  { num: "02", title: "Defining a realistic and usable data foundation", body: "Perfect data doesn't exist – reliable decision-making foundations do. We define management-relevant KPIs, business-relevant conversions, acceptable deviations, and realistic attribution. It's not about absolute accuracy, but about reliable decision-making capability." },
  { num: "03", title: "Optimization of tracking, attribution, and data flow", body: "Based on requirements, we optimize the tracking setup: clean event definitions, cleanup of duplicate signals, sensible attribution models, consideration of consent and iOS restrictions. The goal is not more tracking – but better tracking." },
  { num: "04", title: "Reporting with context instead of number graveyards", body: "Data only realizes its value through context. We ensure that reporting contains the right KPIs, shows developments over time, makes connections visible, and prepares decisions – instead of confusing. Numbers without context are dangerous." },
];

const capabilities = [
  { name: "Tracking audit", detail: "Status check, data quality" },
  { name: "GA4 setup", detail: "Events, conversions, parameters" },
  { name: "Server-side tracking", detail: "Matomo, wetracked, custom" },
  { name: "Consent management", detail: "TCF, privacy-compliant" },
  { name: "Attribution models", detail: "Realistically interpretable" },
  { name: "Reporting architecture", detail: "KPIs with context" },
  { name: "Data flows", detail: "BigQuery, Looker Studio" },
  { name: "Plausibility checks", detail: "Platform vs. web analytics" },
];

const stack = [
  { name: "Google Analytics (GA4)", small: "Web analytics" },
  { name: "Google Tag Manager", small: "Tag management" },
  { name: "Consent management platforms", small: "TCF-compliant" },
  { name: "Looker Studio", small: "Reporting, dashboards" },
  { name: "Matomo", small: "Server-side, privacy-compliant" },
  { name: "wetracked", small: "Specialized server-side setup" },
  { name: "BigQuery", small: "Data warehouse" },
  { name: "Platform APIs", small: "Google, Meta & others" },
];

function TrackingPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Tracking & Data Foundation"
          headline={<>Tracking is <em>not a setup task.</em> It's the foundation.</>}
          lead="Server-side tracking, GA4, BigQuery, attribution modeling. Every action in the system needs unambiguous data — that's where we start."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "Tracking & Data" }]}
        />

        <Section number="02" kicker="Standpoint" headline={<>Numbers should <em>provide orientation.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Many companies make daily decisions based on numbers that are incomplete, distorted, or simply wrong. Platforms report their own success. Privacy and consent distort measurements. Attribution models deliver contradictory results.</p>
              <p className="mb-5">The problem is not missing tracking. The problem is a data foundation that doesn't allow reliable decisions. We ensure that numbers do what they should: provide orientation.</p>
              <p className="mb-5">For us, tracking is not an end in itself and not a technical gimmick. It's the foundation for deciding: which channels make money, which cost money, where scaling is possible, where to slow down. Without this clarity, budget is distributed – not managed.</p>
              <p>How we concretely anchor e-commerce tracking in Shopify — from customer events to server-side tracking and the Conversion API — is shown on the <Link to="/en/services/ecommerce-development/shopify-agency" className="text-cyan-deep underline-offset-4 hover:underline">Shopify agency page</Link>.</p>
            </div>
          </RevealOnScroll>
          <Callout>Perfect data is an illusion. Bad decisions based on false certainty are not.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Approach" headline={<>Four phases to a <em>reliable</em> data foundation.</>}>
          <ProcessFlow steps={steps} />
          <Callout>Platform data shows performance. A clean data foundation shows truth.</Callout>
          <Callout>Whoever manages without server-side tracking is deciding on the basis of incomplete reality.</Callout>
        </Section>

        <Section number="04" kicker="Capabilities & stack" headline={<>What we use to make tracking <em>reliable.</em></>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="en" number="05" />

        <RelatedServices
          locale="en"
          fromId="tracking-datenbasis"
          number="06"
          items={relatedBySource["tracking-datenbasis"]}
        />

        <Section number="07" kicker="Engagement">
          <CTABlock
            headline={<>How <em>reliable</em> are your marketing numbers really?</>}
            body="If you're not sure whether your decisions are based on a reliable data foundation, we should talk."
            primary={{ label: "Get in touch", to: "/en/contact" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
